import { useMutation, useQuery } from '@apollo/client';
import { Button, Chip, IconButton, Tooltip } from '@material-ui/core';
import { FileCopyOutlined } from '@material-ui/icons';
import React, { useCallback } from 'react';

import { hasRole } from 'shared/models/user';

import { client as apolloClient } from '../graphql/apollo-client';
import {
    CREATE_PULSE_ASSESSMENT,
    PULSE_ASSESSMENT_REQUEST,
    PulseAssessmentRequest
} from '../graphql/queries/pulse-assessment-requests';
import { useSession } from '../hooks/use-session';
import { useSnackbar } from '../hooks/use-snackbar';

export const PersonPulseAssessment: React.FC<{ personId: string; jobId: string }> = ({ personId, jobId }) => {
    const { userPermissions } = useSession();
    const canView = hasRole(userPermissions, 'pulse_access');
    const { data, loading, refetch } = useQuery<
        { assessmentRequests: PulseAssessmentRequest[] },
        { personId: string; jobId: string }
    >(PULSE_ASSESSMENT_REQUEST, {
        skip: !canView,
        variables: { personId, jobId }
    });
    const [createAssessment, { loading: creating }] = useMutation<
        { createPulseAssessment: string },
        { personId: string; jobId: string }
    >(CREATE_PULSE_ASSESSMENT, { client: apolloClient('pulse_access') });
    const { setSnackbar } = useSnackbar();

    const handleCreateAssessment = async () => {
        try {
            setSnackbar('Requesting Pulse Assessment...');
            await createAssessment({ variables: { personId, jobId } });
            setSnackbar('Pulse Assessment Requested');
            refetch();
        } catch {
            setSnackbar('Failed to request Pulse Assessment');
        }
    };

    const request = data?.assessmentRequests?.[0];
    const assessmentUrl = request?.assessmentUrl;

    const handleCopyAssessmentUrl = useCallback(async () => {
        if (!assessmentUrl) {
            return;
        }

        try {
            await navigator.clipboard.writeText(assessmentUrl);
            setSnackbar('Copied Pulse Assessment link to clipboard');
        } catch {
            setSnackbar('Failed to copy Pulse Assessment link');
        }
    }, [assessmentUrl, setSnackbar]);

    if (!canView || loading) {
        return null;
    }

    const statusChip =
        request?.status && request.status !== 'pending' ? (
            <Chip label={request.status} size="small" style={{ marginLeft: 8 }} />
        ) : null;

    const createButton = !request ? (
        <Button onClick={handleCreateAssessment} disabled={creating}>
            Send Assessment
        </Button>
    ) : null;

    const content = assessmentUrl ? (
        <div style={{ display: 'inline-flex', alignItems: 'center' }}>
            <Button href={assessmentUrl} target="_blank" rel="noreferrer">
                Pulse Assessment Link
            </Button>
            <Tooltip title="Copy to clipboard">
                <IconButton
                    size="small"
                    onClick={handleCopyAssessmentUrl}
                    aria-label="Copy Pulse Assessment link"
                    style={{ marginLeft: 8 }}
                >
                    <FileCopyOutlined fontSize="small" />
                </IconButton>
            </Tooltip>
            {statusChip}
        </div>
    ) : (
        createButton
    );

    return <div>{content}</div>;
};
